// src/ui/PhilosophyFlowTab.js
// Philosophy Flow Tab Component - Executive Wisdom Collection
// Implements the mockup design with full functionality

import { auth, db, firebase } from '../firebaseConfig.js';

export default class PhilosophyFlowTab {
  constructor() {
    this.quotes = [];
    this.isLoading = false;
  }

  // Main method to get tab content
  async getContent() {
    try {
      const user = auth.currentUser;
      if (!user) {
        return this.createUnauthenticatedContent();
      }

      this.isLoading = true;
      await this.loadQuotes(user.uid);
      this.isLoading = false;

      if (this.quotes.length === 0) {
        return this.createEmptyState();
      }

      return this.createQuotesContent();

    } catch (error) {
      console.error('Error loading Philosophy Flow content:', error);
      return this.createErrorState(error);
    }
  }

  // Load quotes from Firebase
  async loadQuotes(userId) {
    try {
      let quotesSnapshot;
      try {
        // Try with orderBy first
        quotesSnapshot = await db.collection('philosophyQuotes')
          .where('userId', '==', userId)
          .orderBy('createdAt', 'desc')
          .limit(50)
          .get();
      } catch (indexError) {
        console.warn('Firestore index not ready, using simple query:', indexError.message);
        // Fallback without orderBy
        quotesSnapshot = await db.collection('philosophyQuotes')
          .where('userId', '==', userId)
          .limit(50)
          .get();
      }

      this.quotes = [];
      quotesSnapshot.forEach(doc => {
        this.quotes.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        });
      });

      // Sort by date if we couldn't use orderBy
      this.quotes.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      
      console.log(`📚 Loaded ${this.quotes.length} philosophy quotes`);
    } catch (error) {
      console.error('Error loading quotes:', error);
      this.quotes = [];
    }
  }

  // Create content for unauthenticated users
  createUnauthenticatedContent() {
    return `
      <div class="philosophy-flow-content">
        <div class="empty-state">
          <div class="empty-emoji">🎭</div>
          <div class="empty-title">Philosophy Flow</div>
          <div class="empty-description">Sign in to view your philosophical wisdom collection and personal reflections.</div>
        </div>
      </div>
    `;
  }

  // Create empty state when no quotes exist
  createEmptyState() {
    return `
      <div class="philosophy-flow-content">
        <div class="empty-state">
          <div class="empty-emoji">🎭</div>
          <div class="empty-title">Your Wisdom Collection Awaits</div>
          <div class="empty-description">You have saved no quotes yet. Click on New Session to retrieve a quote from your philosophical journey.</div>
          <button class="empty-cta" onclick="sceneManager.switchToScene('PhilosophyFlowScene')">
            ✨ New Session
          </button>
        </div>
      </div>
    `;
  }

  // Create error state
  createErrorState(error) {
    return `
      <div class="philosophy-flow-content">
        <div class="empty-state">
          <div class="empty-emoji">⚠️</div>
          <div class="empty-title">Error Loading Philosophy Flow</div>
          <div class="empty-description">Unable to load your philosophical wisdom collection. Please try again.</div>
          <button class="empty-cta" onclick="location.reload()">
            🔄 Retry Loading
          </button>
        </div>
      </div>
    `;
  }

  // Create main quotes content
  createQuotesContent() {
    const quotesHTML = this.quotes.map(quote => this.createQuoteCard(quote)).join('');
    
    return `
      <div class="philosophy-flow-content">
        <div class="header-section">
          <h1 class="title">
            <span class="title-emoji">🎭</span>
            Executive Wisdom Collection
          </h1>
          
          <div class="stats-row">
            <div class="stat-card">
              <div class="stat-value">${this.quotes.length}</div>
              <div class="stat-label">Total Quotes</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${this.quotes.filter(q => q.favorited).length}</div>
              <div class="stat-label">Favorited</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${this.quotes.filter(q => q.executiveNote).length}</div>
              <div class="stat-label">With Notes</div>
            </div>
          </div>

          <button class="action-button" onclick="sceneManager.switchToScene('PhilosophyFlowScene')">
            <span>✨</span>
            New Session
          </button>
          <div style="clear: both;"></div>
        </div>

        <div class="quotes-grid">
          ${quotesHTML}
        </div>
      </div>
    `;
  }

  // Create individual quote card
  createQuoteCard(quote) {
    const formattedDate = quote.createdAt ? 
      quote.createdAt.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short', 
        day: 'numeric'
      }) : 'Date unknown';

    const emotionText = quote.selectedEmotion?.name || quote.selectedFeeling || 'Unknown mood';
    const sleepText = quote.selectedSleep || 'Unknown sleep';

    return `
      <div class="quote-card" data-quote-id="${quote.id}">
        <div class="quote-text">
          ${quote.quote || 'Quote text not available'}
        </div>
        <div class="author">— ${quote.figure || 'Unknown'}</div>
        
        ${quote.reasoning ? `
          <div class="reasoning-section">
            <div class="section-label">
              <span>🧠</span>
              Strategic Reasoning
            </div>
            <div class="reasoning-text">${quote.reasoning}</div>
          </div>
        ` : ''}
        
        ${quote.executiveNote ? `
          <div class="executive-note">
            <div class="section-label">
              <span>📋</span>
              Executive Reflection
            </div>
            <div class="note-text">${quote.executiveNote}</div>
          </div>
        ` : ''}
        
        <div class="metadata">
          <div class="date">
            ${formattedDate} • ${emotionText} • ${sleepText}
          </div>
          <div class="actions">
            <div class="action-icon ${quote.favorited ? 'favorited' : ''}" data-action="favorite" data-quote-id="${quote.id}">⭐</div>
            <div class="action-icon" data-action="edit-note" data-quote-id="${quote.id}">📝</div>
            <div class="action-icon" data-action="share" data-quote-id="${quote.id}">📤</div>
          </div>
        </div>
      </div>
    `;
  }

  // Get CSS styles for the component
  getStyles() {
    return `
      /* Philosophy Flow Tab Styles - Mockup Implementation */
      .philosophy-flow-content {
        padding: 30px;
        height: calc(100% - 60px);
        overflow-y: auto;
      }

      .header-section {
        margin-bottom: 32px;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        color: #1e293b;
        margin-bottom: 24px;
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .title-emoji {
        font-size: 32px;
      }

      .stats-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin-bottom: 24px;
      }

      .stat-card {
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 20px;
        text-align: center;
        transition: all 0.3s ease;
      }

      .stat-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 212, 255, 0.15);
        border-color: #00d4ff;
      }

      .stat-value {
        font-size: 24px;
        font-weight: 700;
        color: #00d4ff;
        margin-bottom: 4px;
      }

      .stat-label {
        font-size: 11px;
        font-weight: 600;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .action-button {
        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
        color: white;
        border: none;
        padding: 14px 28px;
        border-radius: 10px;
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        float: right;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
      }

      .action-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 212, 255, 0.4);
      }

      .quotes-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 24px;
        margin-top: 32px;
      }

      .quote-card {
        background: white;
        border: 1px solid #e2e8f0;
        border-radius: 16px;
        padding: 24px;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
      }

      .quote-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .quote-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
        border-color: #00d4ff;
      }

      .quote-card:hover::before {
        opacity: 1;
      }

      .quote-card .quote-text {
        font-size: 18px;
        line-height: 1.6;
        color: #1e293b;
        font-style: italic;
        margin-bottom: 16px;
        position: relative;
      }

      .quote-card .quote-text::before {
        content: '"';
        font-size: 24px;
        color: #00d4ff;
        position: absolute;
        left: -8px;
        top: -4px;
      }

      .quote-card .quote-text::after {
        content: '"';
        font-size: 24px;
        color: #00d4ff;
      }

      .quote-card .author {
        font-size: 14px;
        font-weight: 700;
        color: #00d4ff;
        text-align: right;
        margin-bottom: 20px;
      }

      .reasoning-section {
        margin-bottom: 16px;
      }

      .section-label {
        font-size: 10px;
        font-weight: 700;
        color: #00d4ff;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .reasoning-text {
        font-size: 13px;
        line-height: 1.5;
        color: #334155;
        background: #f8fafc;
        padding: 12px;
        border-radius: 8px;
        border-left: 3px solid #00d4ff;
      }

      .executive-note {
        background: #f0fdf4;
        border-left: 3px solid #10b981;
        padding: 12px;
        border-radius: 8px;
        margin-top: 12px;
      }

      .executive-note .section-label {
        color: #10b981;
      }

      .note-text {
        font-size: 13px;
        line-height: 1.5;
        color: #1e293b;
        font-weight: 500;
      }

      .metadata {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #f1f5f9;
      }

      .date {
        font-size: 11px;
        color: #64748b;
        font-weight: 500;
      }

      .actions {
        display: flex;
        gap: 8px;
      }

      .action-icon {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: #f1f5f9;
        border: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        font-size: 14px;
      }

      .action-icon:hover {
        background: #00d4ff;
        color: white;
        transform: scale(1.1);
      }

      .action-icon.favorited {
        background: #fbbf24;
        color: white;
      }

      .empty-state {
        text-align: center;
        padding: 60px 20px;
        max-width: 500px;
        margin: 0 auto;
      }

      .empty-emoji {
        font-size: 64px;
        margin-bottom: 24px;
      }

      .empty-title {
        font-size: 24px;
        font-weight: 700;
        color: #1e293b;
        margin-bottom: 12px;
      }

      .empty-description {
        font-size: 16px;
        color: #64748b;
        line-height: 1.5;
        margin-bottom: 32px;
      }

      .empty-cta {
        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
        color: white;
        border: none;
        padding: 16px 32px;
        border-radius: 12px;
        font-weight: 600;
        font-size: 16px;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3);
      }

      .empty-cta:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 35px rgba(0, 212, 255, 0.4);
      }

      /* Note Editor Styles */
      .note-editor {
        width: 100%;
        min-height: 80px;
        padding: 12px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        font-size: 13px;
        line-height: 1.5;
        color: #1e293b;
        font-family: inherit;
        resize: vertical;
        margin-bottom: 12px;
      }

      .note-editor:focus {
        outline: none;
        border-color: #00d4ff;
        box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
      }

      .note-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
      }

      .note-save-btn, .note-cancel-btn {
        padding: 8px 16px;
        border: none;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .note-save-btn {
        background: #10b981;
        color: white;
      }

      .note-save-btn:hover {
        background: #059669;
        transform: translateY(-1px);
      }

      .note-cancel-btn {
        background: #f1f5f9;
        color: #64748b;
      }

      .note-cancel-btn:hover {
        background: #e2e8f0;
        color: #1e293b;
      }

      @media (max-width: 768px) {
        .philosophy-flow-content {
          padding: 20px;
        }

        .stats-row {
          grid-template-columns: 1fr;
          gap: 12px;
        }

        .quotes-grid {
          grid-template-columns: 1fr;
          gap: 16px;
        }

        .action-button {
          float: none;
          width: 100%;
          justify-content: center;
          margin-top: 16px;
        }

        .title {
          font-size: 24px;
        }
      }
    `;
  }

  // Attach event listeners for interactivity
  attachEventListeners() {
    // Use event delegation for dynamic content
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('action-icon')) {
        const action = e.target.dataset.action;
        const quoteId = e.target.dataset.quoteId;
        
        switch (action) {
          case 'favorite':
            this.toggleFavorite(quoteId, e.target);
            break;
          case 'edit-note':
            this.editNote(quoteId);
            break;
          case 'share':
            this.shareQuote(quoteId);
            break;
        }
      }
    });
  }

  // Toggle favorite status
  async toggleFavorite(quoteId, iconElement) {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const isFavorited = iconElement.classList.contains('favorited');
      
      // Update Firebase
      await db.collection('philosophyQuotes').doc(quoteId).update({
        favorited: !isFavorited,
        lastUpdated: firebase.firestore.FieldValue.serverTimestamp()
      });

      // Update UI
      iconElement.classList.toggle('favorited');
      
      // Update stats
      this.refreshStats();
      
      console.log(`📚 Quote ${quoteId} ${!isFavorited ? 'favorited' : 'unfavorited'}`);
      
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  }

  // Edit note functionality
  editNote(quoteId) {
    const quoteCard = document.querySelector(`[data-quote-id="${quoteId}"]`);
    if (!quoteCard) return;

    const existingNote = quoteCard.querySelector('.note-text');
    const currentNoteText = existingNote ? existingNote.textContent : '';

    // Create inline editor
    const noteSection = quoteCard.querySelector('.executive-note') || this.createNoteSection(quoteCard);
    
    noteSection.innerHTML = `
      <div class="section-label">
        <span>📋</span>
        Executive Reflection
      </div>
      <textarea class="note-editor" placeholder="Add your executive reflection...">${currentNoteText}</textarea>
      <div class="note-actions">
        <button class="note-save-btn">💾 Save</button>
        <button class="note-cancel-btn">✕ Cancel</button>
      </div>
    `;

    const editor = noteSection.querySelector('.note-editor');
    const saveBtn = noteSection.querySelector('.note-save-btn');
    const cancelBtn = noteSection.querySelector('.note-cancel-btn');

    editor.focus();

    saveBtn.addEventListener('click', () => this.saveNote(quoteId, editor.value, noteSection));
    cancelBtn.addEventListener('click', () => this.cancelNoteEdit(quoteId, currentNoteText, noteSection));
  }

  // Create note section if it doesn't exist
  createNoteSection(quoteCard) {
    const noteSection = document.createElement('div');
    noteSection.className = 'executive-note';
    quoteCard.querySelector('.metadata').before(noteSection);
    return noteSection;
  }

  // Save note to Firebase
  async saveNote(quoteId, noteText, noteSection) {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Update Firebase
      await db.collection('philosophyQuotes').doc(quoteId).update({
        executiveNote: noteText.trim(),
        lastUpdated: firebase.firestore.FieldValue.serverTimestamp()
      });

      // Update UI
      if (noteText.trim()) {
        noteSection.innerHTML = `
          <div class="section-label">
            <span>📋</span>
            Executive Reflection
          </div>
          <div class="note-text">${noteText.trim()}</div>
        `;
      } else {
        noteSection.remove();
      }

      // Update stats
      this.refreshStats();
      
      console.log(`📚 Note saved for quote ${quoteId}`);
      
    } catch (error) {
      console.error('Error saving note:', error);
    }
  }

  // Cancel note editing
  cancelNoteEdit(quoteId, originalText, noteSection) {
    if (originalText) {
      noteSection.innerHTML = `
        <div class="section-label">
          <span>📋</span>
          Executive Reflection
        </div>
        <div class="note-text">${originalText}</div>
      `;
    } else {
      noteSection.remove();
    }
  }

  // Share quote functionality
  shareQuote(quoteId) {
    const quoteCard = document.querySelector(`[data-quote-id="${quoteId}"]`);
    if (!quoteCard) return;

    const quoteText = quoteCard.querySelector('.quote-text').textContent;
    const author = quoteCard.querySelector('.author').textContent;
    
    const shareText = `${quoteText}\n\n${author}\n\nShared from DeckOps.ai Philosophy Flow`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareText).then(() => {
      // Show temporary feedback
      const shareIcon = quoteCard.querySelector('[data-action="share"]');
      const originalText = shareIcon.textContent;
      shareIcon.textContent = '✅';
      shareIcon.style.background = '#10b981';
      shareIcon.style.color = 'white';
      
      setTimeout(() => {
        shareIcon.textContent = originalText;
        shareIcon.style.background = '';
        shareIcon.style.color = '';
      }, 2000);
      
      console.log('📚 Quote copied to clipboard');
    }).catch(err => {
      console.error('Error copying to clipboard:', err);
      // Fallback: show quote text in alert
      alert(`Quote copied:\n\n${shareText}`);
    });
  }

  // Refresh statistics in header
  refreshStats() {
    const statCards = document.querySelectorAll('.philosophy-flow-content .stat-card');
    if (statCards.length >= 3) {
      const quoteCards = document.querySelectorAll('.quote-card');
      const totalQuotes = quoteCards.length;
      const favoritedQuotes = document.querySelectorAll('.action-icon.favorited').length;
      const quotesWithNotes = document.querySelectorAll('.executive-note').length;

      statCards[0].querySelector('.stat-value').textContent = totalQuotes;
      statCards[1].querySelector('.stat-value').textContent = favoritedQuotes;
      statCards[2].querySelector('.stat-value').textContent = quotesWithNotes;
    }
  }
}
